<template>
  <section class="trend-viewer">
    <header class="viewer-header">
      <div class="header-summary">
        <div>
          <span class="eyebrow">Watchlist Report</span>
          <h2>{{ report.name }}</h2>
          <div class="header-meta">
            <span class="status-pill" :class="originalStatus">{{ statusLabel(originalStatus) }}</span>
            <span>{{ formattedDate(report.completedAt) }}</span>
          </div>
        </div>

        <button type="button" class="back-button" :disabled="savingStatus || savingReview" @click="$emit('cancel')">
          Back to Watch Trends
        </button>
      </div>

      <div class="header-actions">
        <button type="button" class="mark-reviewed-button" :disabled="loading || savingReview" @click="toggleReviewState">
          {{ savingReview ? "Saving..." : needsReview ? "Mark Reviewed" : "Mark Needs Review" }}
        </button>

        <label class="status-field">
          <span>Trend Status</span>
          <select v-model="selectedStatus" :disabled="savingStatus">
            <option v-for="status in workflowStatuses" :key="status.value" :value="status.value">
              {{ status.label }}
            </option>
          </select>
        </label>

        <button type="button" class="save-status-button" :disabled="savingStatus || !hasStatusChanged" @click="saveStatus">
          {{ savingStatus ? "Saving..." : "Save Status" }}
        </button>
      </div>
    </header>

    <p v-if="statusMessage" class="status-message" :class="{ error: statusSaveFailed }">
      {{ statusMessage }}
    </p>

    <div v-if="loading" class="state-card">Loading completed Watchlist report...</div>
    <div v-else-if="loadError" class="state-card error">{{ loadError }}</div>

    <div v-else class="viewer-scroll">
      <section class="overview-grid">
        <article class="overview-card">
          <span>Reported</span>
          <strong>{{ relativeDate(report.completedAt) }}</strong>
          <small>{{ formattedDate(report.completedAt) }}</small>
        </article>
        <article class="overview-card">
          <span>Answers</span>
          <strong>{{ watchSession.answers?.length ?? 0 }}</strong>
          <small>Structured Watchlist responses</small>
        </article>
        <article class="overview-card">
          <span>Purpose</span>
          <strong>Pattern monitoring</strong>
          <small>Not a Litigation application</small>
        </article>
        <article class="overview-card">
          <span>Last reviewed</span>
          <strong>{{ reportActivity.lastReviewedByName || "Current review" }}</strong>
          <small>{{ optionalDate(reportActivity.lastReviewedDate) }}</small>
        </article>
      </section>

      <section v-if="report.description || report.explainerDescription" class="dashboard-card">
        <div class="section-heading">
          <div>
            <h3>{{ report.explainerTitle || "Watchlist Context" }}</h3>
            <p>This content describes the monitoring topic shown to the App Client.</p>
          </div>
        </div>
        <p class="context-copy">{{ report.explainerDescription || report.description }}</p>
      </section>

      <section class="dashboard-card">
        <div class="section-heading">
          <div>
            <h3>Reported Answers</h3>
            <p>Answers are displayed from the completed Watchlist session.</p>
          </div>
          <span class="count-pill">{{ watchSession.answers?.length ?? 0 }}</span>
        </div>

        <div v-if="orderedAnswers.length" class="answer-list">
          <article v-for="(answer, index) in orderedAnswers" :key="answer.questionId || index" class="answer-card">
            <span class="question-number">{{ index + 1 }}</span>
            <div>
              <p class="question-copy">{{ answer.questionSnapshot || "Watchlist question" }}</p>
              <p class="answer-copy">{{ answer.value || "No answer recorded" }}</p>
              <small v-if="answer.submittedAt">Answered {{ formattedDate(answer.submittedAt) }}</small>
              <div v-if="answer.clarifications?.length" class="client-additions">
                <article v-for="clarification in answer.clarifications" :key="clarification.id" class="client-addition">
                  <span>Client clarification · {{ formattedDate(clarification.createdAt) }}</span>
                  <p>{{ clarification.text }}</p>
                </article>
              </div>
            </div>
          </article>
        </div>
        <div v-else class="empty-state contained">No Watchlist answers were recorded.</div>
      </section>

      <section v-if="watchSession.updates?.length" class="dashboard-card">
        <div class="section-heading">
          <div>
            <h3>Later Client Information</h3>
            <p>Standalone information the client added after completing the Watchlist.</p>
          </div>
          <span class="count-pill">{{ watchSession.updates.length }}</span>
        </div>

        <div class="timeline-list">
          <article v-for="update in watchSession.updates" :key="update.id" class="timeline-card client-update-card">
            <div class="timeline-header">
              <span class="client-pill">Client Update</span>
              <time>{{ formattedDate(update.createdAt) }}</time>
            </div>
            <p>{{ update.text }}</p>
          </article>
        </div>
      </section>

      <section class="dashboard-card">
        <div class="section-heading">
          <div>
            <h3>Internal Notes</h3>
            <p>Private staff analysis for this individual Watchlist report.</p>
          </div>
          <span class="count-pill">{{ notes.length }}</span>
        </div>

        <form class="entry-form" @submit.prevent="saveNote">
          <textarea v-model="noteText" rows="4" placeholder="Add a private trend-review note..." />
          <div class="entry-footer">
            <span>Visible only to authorized staff.</span>
            <button type="submit" :disabled="savingNote || !noteText.trim()">
              {{ savingNote ? "Adding..." : "Add Internal Note" }}
            </button>
          </div>
        </form>

        <div v-if="notes.length" class="timeline-list">
          <article v-for="note in notes" :key="note.id" class="timeline-card">
            <div class="timeline-header">
              <span class="internal-pill">Internal Note</span>
              <span>{{ note.authorName || note.authorId || "Unknown reviewer" }}</span>
              <time>{{ formattedDate(note.createdAt) }}</time>
            </div>
            <p>{{ note.text }}</p>
          </article>
        </div>
        <div v-else class="empty-state contained">No internal notes have been added.</div>
      </section>

      <section class="dashboard-card">
        <div class="section-heading">
          <div>
            <h3>Watchlist Messages</h3>
            <p>Messages are saved with this report. Mobile delivery will be connected separately.</p>
          </div>
          <span class="count-pill">{{ messages.length }}</span>
        </div>

        <form class="entry-form" @submit.prevent="saveMessage">
          <textarea v-model="messageText" rows="4" placeholder="Write a report-specific message..." />
          <div class="message-footer">
            <label>
              <input v-model="messageIntendedForClient" type="checkbox" />
              Intended for the client when delivery is connected
            </label>
            <button type="submit" :disabled="savingMessage || !messageText.trim()">
              {{ savingMessage ? "Saving..." : "Save Message" }}
            </button>
          </div>
        </form>

        <div v-if="messages.length" class="timeline-list">
          <article v-for="message in messages" :key="message.id" class="timeline-card">
            <div class="timeline-header">
              <span :class="message.intendedForClient ? 'client-pill' : 'internal-pill'">
                {{ message.intendedForClient ? "Client Intended" : "Internal Message" }}
              </span>
              <span>{{ message.authorName || message.authorId || "Unknown reviewer" }}</span>
              <time>{{ formattedDate(message.createdAt) }}</time>
            </div>
            <p>{{ message.text }}</p>
          </article>
        </div>
        <div v-else class="empty-state contained">No Watchlist messages have been saved.</div>
      </section>

      <p v-if="activityError" class="activity-error">{{ activityError }}</p>
    </div>
  </section>
</template>

<script>
import { computed, onMounted, ref } from "vue";

import {
  watchTrendWorkflowStatuses,
  watchTrendWorkflowStatusLabel
} from "@/models/watchTrends";
import {
  addWatchTrendMessage,
  addWatchTrendNote,
  fetchWatchTrendMessages,
  fetchWatchTrendNotes,
  fetchWatchTrendSession,
  markWatchTrendNeedsReview,
  markWatchTrendReviewed,
  updateWatchTrendStatus
} from "@/firebase/firestore";

export default {
  name: "TrendsViewer",

  props: {
    report: { type: Object, required: true },
    adminId: { type: String, default: "" },
    adminName: { type: String, default: "" }
  },

  emits: ["cancel", "reviewed", "status-saved"],

  setup(props, { emit }) {
    const watchSession = ref({ answers: [] });
    const reportActivity = ref(props.report);
    const notes = ref([]);
    const messages = ref([]);
    const loading = ref(true);
    const loadError = ref("");
    const activityError = ref("");
    const noteText = ref("");
    const messageText = ref("");
    const messageIntendedForClient = ref(false);
    const savingNote = ref(false);
    const savingMessage = ref(false);
    const originalStatus = ref(props.report.status ?? "completed");
    const selectedStatus = ref(originalStatus.value);
    const savingStatus = ref(false);
    const statusMessage = ref("");
    const statusSaveFailed = ref(false);
    const savingReview = ref(false);
    const workflowStatuses = watchTrendWorkflowStatuses;

    const dateValue = value => {
      if (value?.toDate) { return value.toDate(); }
      if (value instanceof Date) { return value; }
      return value ? new Date(value) : null;
    };

    const formattedDate = value => {
      const date = dateValue(value);
      if (!date) { return "Not recorded"; }
      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit"
      }).format(date);
    };

    const optionalDate = value => value ? formattedDate(value) : "Not recorded";
    const relativeDate = value => {
      const date = dateValue(value);
      if (!date) { return "Unknown"; }
      const hours = Math.max(0, Math.floor((Date.now() - date.getTime()) / 3600000));
      if (hours < 1) { return "Less than an hour ago"; }
      if (hours < 24) { return `${hours} hour${hours === 1 ? "" : "s"} ago`; }
      const days = Math.floor(hours / 24);
      return `${days} day${days === 1 ? "" : "s"} ago`;
    };

    const hasStatusChanged = computed(() => selectedStatus.value !== originalStatus.value);
    const needsReview = computed(() => {
      if (typeof reportActivity.value.needsReview === "boolean") {
        return reportActivity.value.needsReview;
      }
      return !reportActivity.value.lastReviewedDate;
    });
    const orderedAnswers = computed(() => watchSession.value.answers ?? []);

    const loadViewer = async () => {
      loading.value = true;
      loadError.value = "";
      try {
        const [session, loadedNotes, loadedMessages] = await Promise.all([
          fetchWatchTrendSession(props.report),
          fetchWatchTrendNotes(props.report),
          fetchWatchTrendMessages(props.report)
        ]);
        watchSession.value = session;
        notes.value = loadedNotes;
        messages.value = loadedMessages;
      } catch (error) {
        console.error("❌ Unable to load the Watch trend viewer:", error);
        loadError.value = "Unable to load the completed Watchlist report.";
      } finally {
        loading.value = false;
      }
    };

    const toggleReviewState = async () => {
      savingReview.value = true;
      activityError.value = "";
      try {
        const update = needsReview.value ? markWatchTrendReviewed : markWatchTrendNeedsReview;
        const updatedReport = await update(reportActivity.value, {
          adminId: props.adminId,
          adminName: props.adminName
        });
        reportActivity.value = updatedReport;
        emit("reviewed", updatedReport);
      } catch (error) {
        activityError.value = "Unable to update the review state.";
      } finally {
        savingReview.value = false;
      }
    };

    const saveStatus = async () => {
      savingStatus.value = true;
      statusMessage.value = "";
      statusSaveFailed.value = false;
      try {
        const updatedReport = await updateWatchTrendStatus(
          reportActivity.value,
          selectedStatus.value,
          { adminId: props.adminId, adminName: props.adminName }
        );
        reportActivity.value = updatedReport;
        originalStatus.value = updatedReport.status;
        statusMessage.value = `Status saved as ${watchTrendWorkflowStatusLabel(updatedReport.status)}.`;
        emit("status-saved", updatedReport);
      } catch (error) {
        console.error("❌ Unable to update the Watch trend status:", error);
        statusSaveFailed.value = true;
        statusMessage.value = "Unable to save the Watch trend status.";
      } finally {
        savingStatus.value = false;
      }
    };

    const saveNote = async () => {
      savingNote.value = true;
      activityError.value = "";
      try {
        const note = await addWatchTrendNote(props.report, {
          text: noteText.value,
          authorId: props.adminId,
          authorName: props.adminName
        });
        notes.value.unshift(note);
        noteText.value = "";
      } catch (error) {
        activityError.value = "Unable to save the internal note.";
      } finally {
        savingNote.value = false;
      }
    };

    const saveMessage = async () => {
      savingMessage.value = true;
      activityError.value = "";
      try {
        const message = await addWatchTrendMessage(props.report, {
          text: messageText.value,
          authorId: props.adminId,
          authorName: props.adminName,
          intendedForClient: messageIntendedForClient.value
        });
        messages.value.unshift(message);
        messageText.value = "";
        messageIntendedForClient.value = false;
      } catch (error) {
        activityError.value = "Unable to save the Watchlist message.";
      } finally {
        savingMessage.value = false;
      }
    };

    onMounted(loadViewer);

    return {
      watchSession,
      reportActivity,
      notes,
      messages,
      loading,
      loadError,
      activityError,
      noteText,
      messageText,
      messageIntendedForClient,
      savingNote,
      savingMessage,
      originalStatus,
      selectedStatus,
      savingStatus,
      statusMessage,
      statusSaveFailed,
      savingReview,
      workflowStatuses,
      hasStatusChanged,
      needsReview,
      orderedAnswers,
      formattedDate,
      optionalDate,
      relativeDate,
      statusLabel: watchTrendWorkflowStatusLabel,
      toggleReviewState,
      saveStatus,
      saveNote,
      saveMessage
    };
  }
};
</script>

<style scoped>
.trend-viewer { display: flex; flex-direction: column; height: 100%; min-height: 0; }
.viewer-header { display: flex; flex-direction: column; gap: 20px; padding-bottom: 20px; border-bottom: 1px solid #e2e7e4; }
.header-summary { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; }
.eyebrow { color: #159c62; font-size: 0.72rem; font-weight: 800; letter-spacing: 0.11em; text-transform: uppercase; }
.viewer-header h2 { margin: 5px 0 8px; color: #18231f; font-size: 1.55rem; }
.header-meta { display: flex; align-items: center; gap: 10px; color: #78827d; font-size: 0.85rem; }
.header-actions { display: flex; align-items: flex-end; justify-content: flex-end; gap: 9px; width: 100%; }
.status-field { display: flex; flex-direction: column; gap: 5px; color: #68736d; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; }
.status-field select { min-width: 160px; padding: 10px 11px; border: 1px solid #d8dedb; border-radius: 9px; background: #fff; color: #29342f; font: inherit; text-transform: none; }
.back-button, .mark-reviewed-button, .save-status-button { min-height: 42px; padding: 11px 15px; border-radius: 9px; font: inherit; font-weight: 600; white-space: nowrap; cursor: pointer; }
.back-button { border: 1px solid #d8dedb; background: #fff; color: #29342f; }
.mark-reviewed-button { border: 1px solid #0b3652; background: #0b3652; color: #fff; }
.save-status-button { border: 1px solid #19aa6c; background: #19aa6c; color: #fff; }
button:disabled { cursor: not-allowed; opacity: 0.5; }
.status-message { margin: 10px 0 0; color: #178554; font-size: 0.82rem; text-align: right; }
.status-message.error, .activity-error, .state-card.error { color: #a33636; }
.viewer-scroll { min-height: 0; padding: 22px 4px 4px 0; overflow-y: auto; }
.overview-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 13px; }
.overview-card, .dashboard-card { border: 1px solid #dde2df; border-radius: 13px; background: #fff; }
.overview-card { display: flex; flex-direction: column; padding: 17px; }
.overview-card span { color: #7b8580; font-size: 0.73rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; }
.overview-card strong { margin-top: 7px; color: #1d2924; font-size: 1rem; }
.overview-card small { margin-top: 5px; color: #818a86; }
.dashboard-card { margin-top: 18px; padding: 22px; }
.section-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; }
.section-heading h3 { margin: 0; color: #1d2924; font-size: 1.08rem; }
.section-heading p { margin: 5px 0 0; color: #7b8580; font-size: 0.88rem; }
.context-copy { margin: 18px 0 0; color: #37433d; line-height: 1.6; white-space: pre-wrap; }
.count-pill { min-width: 29px; padding: 6px 9px; border-radius: 999px; background: #edf4ef; color: #178554; font-size: 0.76rem; font-weight: 700; text-align: center; }
.answer-list { margin-top: 20px; overflow: hidden; border: 1px solid #e4e8e6; border-radius: 10px; }
.answer-card { display: grid; grid-template-columns: 30px minmax(0, 1fr); gap: 12px; padding: 14px 16px; }
.answer-card + .answer-card { border-top: 1px solid #edf0ee; }
.question-number { display: flex; align-items: center; justify-content: center; width: 25px; height: 25px; border-radius: 50%; background: #edf4ef; color: #178554; font-size: 0.72rem; font-weight: 700; }
.question-copy { margin: 0; color: #65706a; font-size: 0.84rem; }
.answer-copy { margin: 6px 0 0; color: #1d2924; font-weight: 600; white-space: pre-wrap; }
.answer-card small { display: block; margin-top: 7px; color: #8a938e; }
.client-additions { display: grid; gap: 8px; margin-top: 12px; }
.client-addition { padding: 10px 12px; border-left: 3px solid #159c62; border-radius: 0 8px 8px 0; background: #f0f8f4; }
.client-addition span { color: #178554; font-size: 0.72rem; font-weight: 700; }
.client-addition p { margin: 5px 0 0; color: #29342f; white-space: pre-wrap; }
.client-update-card { border-color: #cfe5d8; background: #f7fbf9; }
.entry-form { margin-top: 18px; padding: 16px; border-radius: 10px; background: #f7f9f8; }
.entry-form textarea { width: 100%; box-sizing: border-box; padding: 12px; resize: vertical; border: 1px solid #d8dedb; border-radius: 8px; background: #fff; color: #1d2924; font: inherit; }
.entry-footer, .message-footer { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 11px; color: #7b8580; font-size: 0.82rem; }
.message-footer label { display: flex; align-items: center; gap: 8px; }
.entry-footer button, .message-footer button { padding: 10px 14px; border: 0; border-radius: 8px; background: #19aa6c; color: #fff; font: inherit; font-weight: 700; cursor: pointer; }
.timeline-list { display: grid; gap: 10px; margin-top: 17px; }
.timeline-card { padding: 14px; border: 1px solid #e4e8e6; border-radius: 10px; }
.timeline-header { display: flex; align-items: center; gap: 8px; color: #7b8580; font-size: 0.75rem; }
.timeline-header time { margin-left: auto; }
.timeline-card p { margin: 10px 0 0; color: #29342f; white-space: pre-wrap; }
.internal-pill, .client-pill, .status-pill { padding: 5px 8px; border-radius: 999px; font-size: 0.72rem; font-weight: 700; }
.internal-pill { background: #eef0ef; color: #59635e; }
.client-pill, .status-pill { background: #eaf6ef; color: #178554; }
.status-pill.inReview { background: #fff2e7; color: #ad5514; }
.status-pill.monitoring { background: #e6f3f7; color: #22667a; }
.status-pill.patternIdentified { background: #fff5d8; color: #8b6510; }
.status-pill.escalated { background: #eee9fb; color: #65479b; }
.status-pill.closed { background: #eef0ef; color: #66716b; }
.empty-state, .state-card { margin-top: 18px; padding: 22px; border: 1px solid #e0e5e2; border-radius: 10px; color: #78827d; text-align: center; }
.empty-state.contained { background: #fafbfa; }
.activity-error { margin: 14px 0 0; }
@media (max-width: 1050px) { .overview-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 900px) { .header-summary, .header-actions { align-items: stretch; flex-direction: column; } }
@media (max-width: 700px) { .entry-footer, .message-footer { align-items: stretch; flex-direction: column; } .overview-grid { grid-template-columns: 1fr; } }
</style>
